import type { PatchProxyConfigRequest } from '@onefootprint/request-types/dashboard';
import type { EditSectionId, FormData } from '@/pages/developers/vault-proxy/pages/list/vault-proxy.types';

/**
 * Creates a PatchProxyConfigRequest payload for updating a specific section
 */
const formatUpdatePayload = (
  stepId: EditSectionId,
  formData: FormData,
  originalSecretHeaders: { id: string; name: string }[],
): PatchProxyConfigRequest => {
  switch (stepId) {
    case 'base-config':
      return {
        name: formData.name,
        method: formData.method,
        url: formData.url,
        accessReason: formData.accessReason,
      };

    case 'custom-header': {
      // A secret row with a value is a new or replacement secret; existing secrets load
      // blank and are left as-is.
      const addSecretHeaders = formData.headers
        .filter(header => header.secret && header.name && header.value)
        .map(header => ({ name: header.name, value: header.value }));

      // Keep an original secret only if its row is still secret and still blank. Anything
      // else — removed, value replaced, or un-secreted — is deleted (and re-added above or
      // as a plaintext header below when a value was entered).
      const untouchedIds = new Set(
        formData.headers.filter(header => header.id && header.secret && !header.value).map(header => header.id),
      );
      const deleteSecretHeaders = originalSecretHeaders
        .filter(header => !untouchedIds.has(header.id))
        .map(header => header.id);

      return {
        // BE bug doesn't allow only updating headers and addSecretHeaders fields, so also sending the name
        // TODO: remove once fixed
        name: formData.name,
        headers: formData.headers
          .filter(header => !header.secret && header.name && header.value)
          .map(header => ({ name: header.name, value: header.value })),
        addSecretHeaders,
        deleteSecretHeaders,
      };
    }

    case 'client-identity':
      return formData.clientIdentity.certificate && formData.clientIdentity.key
        ? {
            clientIdentity: {
              certificate: formData.clientIdentity.certificate,
              key: formData.clientIdentity.key,
            },
          }
        : { clientIdentity: undefined }; // TODO: deletion bug, BE to fix

    case 'server-certificates':
      return {
        //BE bug doesn't allow only updating pinnedServerCertificates field, so also sending the name
        // TODO: remove once fixed
        name: formData.name,
        pinnedServerCertificates: formData.pinnedServerCertificates
          .filter(({ certificate }) => certificate)
          .map(({ certificate }) => certificate),
      };

    case 'ingress-vaulting':
      return {
        ingressSettings:
          formData.ingressSettings.contentType === 'json'
            ? {
                contentType: formData.ingressSettings.contentType,
                rules: formData.ingressSettings.rules,
              }
            : undefined, // TODO: deletion bug, BE to fix
      };

    default:
      return {};
  }
};

export default formatUpdatePayload;
